/**
 * Lifecycle & Functions — key functions, detailed functions split
 * by criticality, lifecycle phases, operational modes. Pulls from
 * `formatted.lifecycle_and_functions`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, ObjectListField, type SectionProps } from './parts';

const PATH = ['lifecycle_and_functions'];

const yesNo = (v) => (v === true ? 'true' : v === false ? 'false' : '');
const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v);

export default function LifecycleFunctions({ data, isEditing, onChange }: SectionProps) {
  const lc = data ?? {};
  const allFunctions = lc.detailed_functions?.all_functions ?? [];
  const lifecyclePhases = lc.lifecycle_phases?.phases ?? [];
  const operationalModes = lc.operational_modes?.modes ?? [];

  const functionColumns = [
    { key: 'function_name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'criticality', label: 'Criticality' },
    { key: 'related_components', label: 'Components', render: joinList },
    { key: 'safety_relevant', label: 'Safety Relevant', render: yesNo },
    { key: 'security_relevant', label: 'Security Relevant', render: yesNo },
  ];

  const phaseColumns = [
    { key: 'phase_name', label: 'Name' },
    { key: 'behavior_description', label: 'Description' },
    { key: 'active_functions', label: 'Active Functions', render: joinList },
    { key: 'security_considerations', label: 'Security Considerations', render: joinList },
    { key: 'entry_conditions', label: 'Entry Conditions', render: joinList },
    { key: 'exit_conditions', label: 'Exit Conditions', render: joinList },
  ];

  const modeColumns = [
    { key: 'mode', label: 'Mode' },
    { key: 'description', label: 'Description' },
    { key: 'active_functions', label: 'Active Functions', render: joinList },
    { key: 'security_state', label: 'Security State' },
    { key: 'entry_conditions', label: 'Entry Conditions', render: joinList },
    { key: 'exit_conditions', label: 'Exit Conditions', render: joinList },
    { key: 'allowed_transitions', label: 'Allowed Transitions', render: joinList },
  ];

  return (
    <SectionCard title="Lifecycle & Functions" id="lifecycle_and_functions">
      <SubSection title="Detailed functions" count={allFunctions.length}>
        <ObjectListField columns={functionColumns} rows={allFunctions} isEditing={isEditing} onChange={onChange} path={[...PATH, 'detailed_functions', 'all_functions']} />
      </SubSection>

      <SubSection title="Lifecycle phases" count={lifecyclePhases.length}>
        <ObjectListField columns={phaseColumns} rows={lifecyclePhases} isEditing={isEditing} onChange={onChange} path={[...PATH, 'lifecycle_phases', 'phases']} />
      </SubSection>

      <SubSection title="Operational modes" count={operationalModes.length}>
        <ObjectListField columns={modeColumns} rows={operationalModes} isEditing={isEditing} onChange={onChange} path={[...PATH, 'operational_modes', 'modes']} />
      </SubSection>
    </SectionCard>
  );
}
